/**
 * POs late to raise (CR-8623): forecast rows past their order-by date, and the
 * order history behind each one.
 *
 * @link backend/purchasing/views/late_to_raise_views.py
 */
import api, { type AxiosResponse } from './client';

/** Days-late band for the Late filter; '' = all. */
export type LateBand = '' | '0-7' | '8-30' | '31-';

export interface LateToRaiseParams {
  search?: string;
  late?: LateBand;
  /** Drop parts with no PO history. The server defaults to true. */
  hide_never_ordered?: boolean;
  /** CR-8668: '<field>' ascending or '-<field>' descending; absent = most overdue first. */
  sort?: string;
  page?: number;
  page_size?: number;
}

export interface LateToRaiseRow {
  /** The forecast row's id — the key for its order history. */
  id: number;
  customer_id: number;
  customer_name: string;
  part_number: string;
  description: string;
  qty_to_order: string;
  unit_of_measure: string;
  /** ISO date the order should have been placed by. */
  order_by: string;
  days_late: number;
  order_count: number;
  supplier_count: number;
  /** Unit price of the newest PO line that was not cancelled; null when none. */
  last_price: string | null;
  last_price_currency: string;
}

export interface LateToRaiseListResponse {
  count: number;
  results: LateToRaiseRow[];
}

export interface OrderHistoryParams {
  supplier?: number;
  page?: number;
  page_size?: number;
}

export interface OrderHistoryLine {
  id: number;
  po_number: string;
  line_number: number;
  supplier_id: number | null;
  supplier_name: string;
  issue_date: string | null;
  quantity: string;
  unit_of_measure: string;
  unit_price: string;
  currency: string;
  /** Against the same supplier's previous non-cancelled line in the same currency and UOM; null = no arrow. */
  price_change: string | null;
  status: string;
  status_label: string;
  received_date: string | null;
}

export interface OrderHistorySupplier {
  id: number | null;
  name: string;
  count: number;
}

export interface OrderHistoryResponse {
  part_number: string;
  /** Lines across every supplier, whatever the supplier filter. */
  total: number;
  suppliers: OrderHistorySupplier[];
  /** Lines matching the supplier filter. */
  count: number;
  results: OrderHistoryLine[];
}

export const lateToRaiseAPI = {
  list: (params: LateToRaiseParams): Promise<AxiosResponse<LateToRaiseListResponse>> =>
    api.get('/purchasing/late-to-raise/', { params }),
  export: (params: LateToRaiseParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/late-to-raise/export/', { params, responseType: 'blob' }),
  history: (id: number, params: OrderHistoryParams): Promise<AxiosResponse<OrderHistoryResponse>> =>
    api.get(`/purchasing/late-to-raise/${id}/history/`, { params }),
  historyExport: (id: number, params: OrderHistoryParams): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/late-to-raise/${id}/history-export/`, { params, responseType: 'blob' }),
};
